"use client";

import type { FC, ReactNode } from "react";
import { useState } from "react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import * as RadixPopover from "@radix-ui/react-popover";
import { Calendar } from "@/components/calendar";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, type FieldState } from "./field-chrome";

type DateInputProps = {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  value?: Date;
  defaultValue?: Date;
  onValueChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  /** Earliest selectable day. Days before it are greyed out in the picker rather than rejected
   *  after the fact — the field's own schema still has to enforce it. */
  minDate?: Date;
  /** Latest selectable day. Also bounds the picker's year dropdown, which otherwise stops at the
   *  current year and can't reach a future date. Defaults to 20 years out. */
  maxDate?: Date;
  id?: string;
};

const yearsFromNow = (years: number): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + years);
  return date;
};

export const DateInput: FC<DateInputProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  placeholder = "Please input",
  value,
  defaultValue,
  onValueChange,
  disabled,
  minDate,
  maxDate,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<Date | undefined>(defaultValue);
  const selected = value ?? internal;

  const handleSelect = (date: Date | undefined) => {
    setInternal(date);
    onValueChange?.(date);
    setOpen(false);
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={open} onOpenChange={setOpen}>
        <RadixPopover.Trigger
          id={id}
          disabled={disabled}
          className={cn(
            "flex w-full cursor-pointer items-center justify-between gap-8 rounded-medium border bg-neutral-white p-16 text-left outline-none focus:border-brand-blue disabled:cursor-not-allowed disabled:bg-neutral-black/5",
            state === "error" ? "border-brand-red" : "border-neutral-mid-grey",
            selected ? "text-neutral-black" : "text-neutral-black/35",
            className,
          )}
        >
          <span className="text-16">
            {selected ? selected.toLocaleDateString() : placeholder}
          </span>
          <CalendarDaysIcon className="size-24 shrink-0 text-neutral-black" />
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            sideOffset={8}
            className="z-50 rounded-medium border border-neutral-light-grey bg-neutral-white p-16 shadow-lg"
          >
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={selected}
              onSelect={handleSelect}
              defaultMonth={selected}
              startMonth={minDate ?? yearsFromNow(-100)}
              endMonth={maxDate ?? yearsFromNow(20)}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
            />
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
